import React, { useState } from 'react';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';
import type { AwardPaymentRequest } from '../../types/awardPayment';
import type { AwardPaymentRequestSectionProps, AwardPaymentRequestsProps } from './AwardPaymentRequests.types';
import { getStatusColorClass } from '../../utils/awardStatusColor';
import PATH from '../../constants/paths';
import { COMPLETED_STATUSES, normalizePaymentRequestStatus, toDisplayStatus } from './paymentRequestStatuses';
import AwardPaymentReviewerTable from './AwardPaymentReviewerTable';
import AwardSummaryTilesSection from './award-summary-tiles-section';
import { getAmountRequestedDisplay } from './awardUtils';

const isCompletedRequest = (request: AwardPaymentRequest) => COMPLETED_STATUSES.has(normalizePaymentRequestStatus(request.status));

const styles = {
  sectionCard: 'w-full p-4 sm:p-6 md:p-8 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-brand-cyan flex flex-col items-start',
  heading: 'text-blue text-2xl font-normal font-serif leading-9 sm:text-center md:text-center lg:text-left',
  itemWrapper: 'w-full',
  itemCard: 'w-full p-4 sm:p-6 rounded-lg outline outline-1 outline-offset-[-1px] outline-brand-border hover:bg-gray-50 cursor-pointer',
  itemGrid: 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 xl:items-center',
  fieldLabel: 'text-brand-navy text-sm font-semibold font-sans leading-5',
  fieldValue: 'pt-1 text-gray-800 text-md font-normal font-sans leading-6',
  statusValue: 'pt-1 text-md font-semibold font-sans leading-6',
  viewCell: 'xl:justify-self-end',
  viewButton:
    'w-full xl:w-auto px-6 py-3 rounded-[999px] outline outline-2 outline-offset-[-2px] outline-purple inline-flex justify-center items-center gap-2',
  viewText: 'text-center text-purple text-sm font-bold font-sans uppercase leading-4 tracking-wide',
  sectionSpacing: 'pt-8',
  emptyStateMessage: 'w-full text-center text-brand-navy text-md font-normal font-sans leading-6',
};

const AwardPaymentRequestSection = ({ heading, requests, onViewRequest, setIsLoading }: AwardPaymentRequestSectionProps) => {
  if (!requests.length) {
    return null;
  }

  return (
    <div className={cn(styles.sectionCard)}>
      <div className='w-full'>
        <h2 className={cn(styles.heading)}>{heading}</h2>
      </div>

      <div className='w-full pt-6'>
        <div className='w-full space-y-4'>
          {requests.map((request) => (
            <div key={request.id} className={cn(styles.itemWrapper)}>
              <div
                className={cn(styles.itemCard)}
                onClick={() => {
                  setIsLoading?.(true);
                  onViewRequest(request.id, request.userId);
                }}
              >
                <div className={cn(styles.itemGrid)}>
                  <div>
                    <p className={cn(styles.fieldLabel)}>Type</p>
                    <p className={cn(styles.fieldValue)}>{request.type.charAt(0).toUpperCase() + request.type.slice(1)}</p>
                  </div>
                  <div>
                    <p className={cn(styles.fieldLabel)}>Date Submitted</p>
                    <p className={cn(styles.fieldValue)}>{request.submittedAt ? request.submittedAt : 'N/A'}</p>
                  </div>
                  <div>
                    <p className={cn(styles.fieldLabel)}>Amount Requested</p>
                    <p className={cn(styles.fieldValue)}>{getAmountRequestedDisplay(request)}</p>
                  </div>
                  <div>
                    <p className={cn(styles.fieldLabel)}>Status</p>
                    <p className={cn(styles.statusValue, getStatusColorClass(request.status))}>{toDisplayStatus(request.status)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AwardPaymentRequests = ({ allRequests, isReviewerView = false, setIsLoading }: AwardPaymentRequestsProps) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const history = useHistory();
  const handleViewRequest = (id: number, userId?: number) => {
    setIsLoading?.(true);
    const query = userId && userId > 0 ? `?id=${id}&userId=${userId}` : `?id=${id}`;
    history.push(`${PATH.AWARD_PAYMENT_REQUEST}${query}`);
  };

  if (!allRequests.length) {
    return (
      <div className={cn(styles.sectionCard)}>
        <p className={cn(styles.emptyStateMessage)}>There are no requests.</p>
      </div>
    );
  }

  const handleSelectStatus = (status: string) => {
    window.scrollTo({ top: 1, behavior: 'smooth' });
    if (status === selectedStatus) {
      setSelectedStatus('');
    } else {
      setSelectedStatus(status);
    }
  };

  if (isReviewerView) {
    return (
      <div className='w-full flex flex-col gap-6 mt-3'>
        <AwardSummaryTilesSection requests={allRequests} onSelectStatus={handleSelectStatus} selectedStatus={selectedStatus} />
        <AwardPaymentReviewerTable
          allRequests={allRequests}
          selectedStatus={selectedStatus}
          onClearSelectedStatus={() => {
            if (selectedStatus) setSelectedStatus('');
          }}
          setIsLoading={setIsLoading}
        />
      </div>
    );
  }

  const inProgressRequests = allRequests.filter((request) => !isCompletedRequest(request));
  const completedRequests = allRequests.filter((request) => isCompletedRequest(request));

  return (
    <>
      <AwardPaymentRequestSection
        heading='Requests In Progress'
        requests={inProgressRequests}
        onViewRequest={handleViewRequest}
        setIsLoading={setIsLoading}
      />
      <div className={cn(styles.sectionSpacing)}>
        <AwardPaymentRequestSection
          heading='Completed Requests'
          requests={completedRequests}
          onViewRequest={handleViewRequest}
          setIsLoading={setIsLoading}
        />
      </div>
    </>
  );
};

export default AwardPaymentRequests;
